<script setup lang="ts">
/**
 * POST /usage/estimate: a dry-run projection of what a billable operation would cost and the
 * balance that would remain — nothing is recorded. The operation kinds and their natural units
 * come from the contract: prediction rows, training minutes, stored bytes, chat tokens.
 */
import type { components } from '@scalty/contracts'
import Dialog from '~/components/ui/Dialog.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'
import Field from '~/components/ui/Field.vue'
import SelectField from '~/components/ui/SelectField.vue'
import ParamRow from '~/components/inspector/ParamRow.vue'

type Kind = components['schemas']['UsageKind']
const props = defineProps<{ deployments: { slug: string; name: string }[]; result: { cost: string; remaining: string; beyond: boolean } | null; pending: boolean; failure: string | null }>()
const emit = defineEmits<{ close: []; estimate: [payload: { kind: Kind; quantity: number; deploymentSlug: string | null }] }>()

const KINDS: { value: Kind; label: string; unit: string; factor: number }[] = [
  { value: 'prediction', label: 'Predictions', unit: 'rows', factor: 1 },
  { value: 'training', label: 'Training', unit: 'minutes', factor: 1 },
  { value: 'storage', label: 'Storage', unit: 'gigabytes', factor: 1_000_000_000 },
  { value: 'input_tokens', label: 'Chat · input tokens', unit: 'tokens', factor: 1 },
  { value: 'output_tokens', label: 'Chat · output tokens', unit: 'tokens', factor: 1 },
]
const kind = ref<Kind>('prediction')
const amount = ref('1000')
const deployment = ref(props.deployments[0]?.slug ?? '')
/* The endpoints arrive after the dialog opens; the first one is preselected once. */
watch(() => props.deployments, (list) => { if (!deployment.value && list[0]) deployment.value = list[0].slug }, { immediate: true })
const chosen = computed(() => KINDS.find((entry) => entry.value === kind.value) ?? KINDS[0]!)
const quantity = computed(() => Math.round(Number(amount.value) * chosen.value.factor))
const needsDeployment = computed(() => kind.value === 'prediction')
const complete = computed(() => Number.isFinite(quantity.value) && quantity.value >= 1 && (!needsDeployment.value || Boolean(deployment.value)))
</script>

<template>
  <Dialog title="Project a cost" meta="nothing is recorded" :width="520" @close="emit('close')">
    <template #icon><Icon name="calculator" /></template>
    <div class="grid gap-3 md:grid-cols-2">
      <SelectField v-model="kind" label="Operation" :options="KINDS.map((entry) => ({ value: entry.value, label: entry.label }))" />
      <Field v-model="amount" :label="'Quantity · ' + chosen.unit" type="number" inputmode="numeric" />
    </div>
    <SelectField v-if="needsDeployment" v-model="deployment" label="Deployment" :options="deployments.length ? deployments.map((entry) => ({ value: entry.slug, label: entry.name })) : [{ value: '', label: 'No deployment in this project' }]" hint="Prediction pricing follows the deployment that serves the call" />
    <p v-if="failure" role="alert" class="m-0 text-xs text-destructive">{{ failure }}</p>
    <div v-else-if="result" class="flex flex-col gap-[var(--measure-7px)] rounded-md border border-border p-2.5">
      <ParamRow label="Projected cost" :value="result.cost" />
      <ParamRow label="Balance after" :value="result.remaining" />
      <!-- The projection fitting or not is the answer the dialog exists to give, so it is said
           in words rather than appended to a figure that reads the same either way. -->
      <div v-if="result.beyond" class="flex items-start gap-2 rounded-md border border-warning/40 px-2.5 py-2 text-xs"><Icon name="alert" :size="12" class="mt-0.5 shrink-0 text-warning" /><span>This operation does not fit: it takes the month past the limit in effect, and work stops when the limit is reached.</span></div>
    </div>
    <template #actions>
      <ActionButton label="Close" @click="emit('close')" />
      <ActionButton :label="pending ? 'Projecting…' : 'Project cost'" variant="primary" :disabled="!complete || pending" @click="emit('estimate', { kind, quantity, deploymentSlug: needsDeployment ? deployment : null })"><template #icon><Icon name="calculator" /></template></ActionButton>
    </template>
  </Dialog>
</template>
